import { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from './useContract';

const CACHE_TTL_MS = 30_000; // 30s TTL so admin XT-price updates propagate
const CHANGE_EVENT = 'xterra:xt-price-change';

let CACHE = null;
let CACHE_AT = 0;
let INFLIGHT = null;

export function invalidateXtPriceCache() {
  CACHE = null;
  CACHE_AT = 0;
  INFLIGHT = null;
  // Broadcast so every mounted useXtPrice() consumer refetches immediately.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }
}

/**
 * Fetch the on-chain XT price in USDT (18 decimals).
 * Contract: xtPriceUSDT = USDT per 1 XT. So xt = usdt * 1e18 / xtPrice.
 */
export function useXtPrice() {
  const { read } = useContracts();
  const cacheFresh = CACHE && (Date.now() - CACHE_AT) < CACHE_TTL_MS;
  const [xtPrice, setXtPrice] = useState(cacheFresh ? CACHE : ethers.utils.parseUnits('1', 18));
  const [loading, setLoading] = useState(!cacheFresh);
  const [tick, setTick] = useState(0);

  // Force a refetch whenever an invalidation event fires (admin updated the price).
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    window.addEventListener(CHANGE_EVENT, bump);
    return () => window.removeEventListener(CHANGE_EVENT, bump);
  }, []);

  useEffect(() => {
    let mounted = true;
    const isFresh = CACHE && (Date.now() - CACHE_AT) < CACHE_TTL_MS;

    if (isFresh) {
      setXtPrice(CACHE);
      setLoading(false);
      return;
    }

    const promise = INFLIGHT || (INFLIGHT = read.storage.xtPriceUSDT()
      .then((p) => {
        if (p && p.gt(0)) {
          CACHE = p;
          CACHE_AT = Date.now();
        }
        INFLIGHT = null;
        return CACHE;
      })
      .catch(() => {
        INFLIGHT = null;
        return CACHE;
      }));

    setLoading(true);
    promise.then((p) => {
      if (!mounted) return;
      if (p) setXtPrice(p);
      setLoading(false);
    });

    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  return { xtPrice, loading };
}
